import * as monaco from "monaco-editor";

import { getEntityByQualifiedName, getEntityClass } from "../api";
import { processLiquidTemplate } from "../services/integrations/process-liquid-template";
import {
  entityPlaceholderRegex,
  liquidTemplatePlaceholderRegex,
} from "./regex-definitions";

// Cache hover results for better performance
const hoverCache = new Map<string, monaco.languages.Hover>();

export const entityHoverProvider: monaco.languages.HoverProvider = {
  provideHover: async (model, position) => {
    // Quick check if we're even in a position that could be an entity
    const lineContent = model.getLineContent(position.lineNumber);

    // Find all matches in the line
    const matches = Array.from(lineContent.matchAll(entityPlaceholderRegex));
    for (const match of matches) {
      const matchStart = match.index!;
      const matchEnd = matchStart + match[0].length;

      // Check if cursor is within this match
      if (
        position.column >= matchStart + 1 &&
        position.column <= matchEnd + 1
      ) {
        const entityName = match[1]?.trim();
        if (!entityName) return null;

        // Check cache first
        const cached = hoverCache.get(entityName);
        if (cached) {
          return {
            ...cached,
            range: new monaco.Range(
              position.lineNumber,
              matchStart + 1,
              position.lineNumber,
              matchEnd + 1,
            ),
          };
        }

        try {
          const result = await getEntityByQualifiedName({
            path: { qualifiedName: entityName },
          });

          if (result.error) {
            console.error(result.error);
            return null;
          }

          const entity = result.data;
          if (entity) {
            const entityClass = await getEntityClass({
              path: { id: entity.entityClassId },
            });
            const hover = {
              contents: [
                { value: `### Entity: ${entity.name}` },
                {
                  value: `**Class:** ${entityClass.data ? entityClass.data.name : ""}`,
                },
              ],
              range: new monaco.Range(
                position.lineNumber,
                matchStart + 1,
                position.lineNumber,
                matchEnd + 1,
              ),
            };

            // Cache the hover result
            hoverCache.set(entityName, hover);

            return hover;
          }
        } catch (error) {
          console.error("Error fetching entity info:", error);
          return null;
        }
      }
    }

    return null;
  },
};

export const liquidTemplateHoverProvider: monaco.languages.HoverProvider = {
  provideHover: async (model, position) => {
    const lineContent = model.getLineContent(position.lineNumber);
    // Find all matches in the line
    const matches = Array.from(
      lineContent.matchAll(liquidTemplatePlaceholderRegex),
    );
    for (const match of matches) {
      const matchStart = match.index!;
      const matchEnd = matchStart + match[0].length;

      // Check if cursor is within this match
      if (
        position.column >= matchStart + 1 &&
        position.column <= matchEnd + 1
      ) {
        const liquidTemplate = match[1]?.trim();
        if (!liquidTemplate) return null;

        // Check cache first
        const cached = hoverCache.get(liquidTemplate);
        if (cached) {
          return {
            ...cached,
            range: new monaco.Range(
              position.lineNumber,
              matchStart + 1,
              position.lineNumber,
              matchEnd + 1,
            ),
          };
        }

        try {
          const result = await new Promise<string>((resolve, reject) => {
            const processFn = processLiquidTemplate(
              `{{ ${liquidTemplate} }}`,
              (result) => resolve(result),
              (error) => reject(new Error(error)),
            );
            processFn();
          });

          const hover = {
            contents: [
              { value: `### Liquid Template Result` },
              {
                value: `\`\`\`\n${result}\n\`\`\``,
              },
            ],
            range: new monaco.Range(
              position.lineNumber,
              matchStart + 1,
              position.lineNumber,
              matchEnd + 1,
            ),
          };

          // Cache the hover result
          hoverCache.set(liquidTemplate, hover);

          return hover;
        } catch (error) {
          console.error("Error processing liquid template:", error);
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error occurred";
          const hover = {
            contents: [
              { value: `### Liquid Template Error` },
              {
                value: `**Error:** ${errorMessage}`,
              },
            ],
            range: new monaco.Range(
              position.lineNumber,
              matchStart + 1,
              position.lineNumber,
              matchEnd + 1,
            ),
          };
          // Cache error result too to avoid repeated processing
          hoverCache.set(liquidTemplate, hover);
          return hover;
        }
      }
    }
    return null;
  },
};
